import { NetworkService } from './network-service';

const API_BASE_URL = process.env.REACT_APP_API_EXECUTION_ADDRESS;

export default class ProjectService {
    /**
     * Get all projects
     * @returns {Promise<Array>} Array of projects
     */
    static async getProjectsAsync() {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/list`);
        return response.items || response || [];
    }

    /**
     * Get single project by ID
     * @param {string|number} id - Project ID
     * @returns {Promise<Object>} Project data
     */
    static async getProjectAsync(id) {
        return await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}`);
    }

    /**
     * Get project team members
     * @param {string|number} id - Project ID
     * @returns {Promise<Array>} Team members array
     */
    static async getProjectTeamAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/team`);
        return response.items || response || [];
    }

    static async createProjectTeamInvitationsBulkAsync(id, body) {
        const response = await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/invitations/bulk`, body);
        return response.results || response || [];
    }

    static async createProjectTeamInvitationAcceptAsync(body) {
        const response = await NetworkService.postAsync(`${API_BASE_URL}/projects/invitations/accept`, body);
        return response.results || response || [];
    }

    /**
     * Create or update project
     * @param {FormData} formData - Project data
     * @returns {Promise<Object>} Created/updated project
     */
    static async createUpdateAsync(formData) {
        return await NetworkService.postMultiPartAsync(`${API_BASE_URL}/projects/upsert`, formData);
    }

    /**
     * Partially update a project — accepts title, deadline, archived, status.
     * Preferred over createUpdateAsync for single-field edits: no multipart
     * body and no need to resend every field.
     * @param {string|number} id - Project ID
     * @param {Object} body - subset of { title, deadline, archived, status }
     * @returns {Promise<Object>} Updated project
     */
    static async patchProjectAsync(id, body) {
        return await NetworkService.patchAsync(`${API_BASE_URL}/projects/${id}`, body);
    }

    /**
     * Mark a project's messages as seen, which resets its unread counter.
     * @param {string|number} id - Project ID
     */
    static async markProjectSeenAsync(id) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/seen`, {});
    }

    static async getProjectCueSheetAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/cue-sheet`);
        return response.items || response || [];
    }

    static async createProjectCueSheetItemAsync(id, formData) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/cue-sheet`, formData);
    }

    static async importProjectCueSheetItemsAsync(id, formData) {
        return await NetworkService.postMultiPartAsync(`${API_BASE_URL}/projects/${id}/cue-sheet/import`, formData);
    }

    static async deleteProjectCueSheetItemAsync(id, itemId) {
        return await NetworkService.deleteAsync(`${API_BASE_URL}/projects/${id}/cue-sheet/${itemId}`);
    }

    /**
     * Removes several cue sheet items in one request.
     * @param {string[]} ids up to 100 item ids
     * @returns {Promise<{results:{cueSheetId:string,deleted:boolean,error?:string}[]}>}
     */
    static async bulkDeleteProjectCueSheetItemsAsync(id, ids) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/cue-sheet/bulk-delete`, { ids });
    }

    /**
     * Replaces the assignees of one cue sheet item — the list that is sent is
     * the list that stays, so the caller never has to compute a diff. An empty
     * list unassigns everybody and takes the item back to `to_work`.
     *
     * @param {string[]} userIds
     * @param {string|null} note optional, reaches every newly added assignee
     * @returns {Promise<object>} the updated CueSheetItemRead
     */
    static async setProjectCueSheetAssigneesAsync(id, itemId, userIds, note = null) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/projects/${id}/cue-sheet/${itemId}/assignees`,
            { user_ids: userIds, ...(note ? { note } : {}) }
        );
    }

    /** Drops one assignee, leaving the others in place. */
    static async removeProjectCueSheetAssigneeAsync(id, itemId, userId) {
        return await NetworkService.deleteAsync(
            `${API_BASE_URL}/projects/${id}/cue-sheet/${itemId}/assignees/${userId}`
        );
    }

    /**
     * The same replace-set over a group of items, in one request.
     * @param {{cue_sheet_id:string,user_ids:string[]}[]} items
     */
    static async bulkSetProjectCueSheetAssigneesAsync(id, items, note = null) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/projects/${id}/cue-sheet/assignees/bulk`,
            { items, ...(note ? { note } : {}) }
        );
    }

    static async updateProjectCueSheetItemsAsync(projectId, payload) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/projects/${projectId}/cue-sheet/bulk-with-history`,
            payload
        );
    }

    static async cutProjectCueSheetItemAsync(projectId, itemId, firstCut, secondCut) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/projects/${projectId}/cue-sheet/${itemId}/cut/v2`,
            { first_cut: firstCut, second_cut: secondCut }
        );
    }

    static async getProjectHistoryAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/edit-history`);
        return response.items || response || [];
    }

    static async analyzeSyncPointsAsync(id, videoId) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/videos/${videoId}/sync-points`, {});
    }

    static async getProjectVideosAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/videos/list`);
        return response.items || response || [];
    }

    static async createProjectVideoItemAsync(id, formData) {
        return await NetworkService.postMultiPartAsync(`${API_BASE_URL}/projects/${id}/videos`, formData);
    }

    static async getProjectVideoItemAsync(id, videoId) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/videos/${videoId}`);
        return response.items || response || [];
    }

    static async deleteProjectVideoItemAsync(id, videoId) {
        return await NetworkService.deleteAsync(`${API_BASE_URL}/projects/${id}/videos/${videoId}`);
    }

    static async getProjectMessagesAsync(id, params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/messages${query ? '?' + query : ''}`);
        return response;
    }

    static async createProjectMessageAsync(id, body, params) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${id}/messages`, body, params);
    }

    /*static async getProjectClipsAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/clip/list`);
        return response.items || response || [];
    }

    static async getProjectTimelineAsync(id) {
        const response = await NetworkService.getAsync(`${API_BASE_URL}/projects/${id}/timeline`);
        return response || {};
    }

    static async saveProjectTimelineAsync(id, data) {
        return await NetworkService.putAsync(`${API_BASE_URL}/projects/${id}/timeline`, data);
    }*/
}
